import {
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type {
  PurchaseTrendForecastUnavailableReason,
  PurchaseTrendUnmatchedSupplier,
} from '@erp/rpc';
import { Num, Plural, T, useGT } from 'gt-react';
import { useQueryClient } from '@tanstack/react-query';
import { CircleNotchIcon } from '@phosphor-icons/react';
import type { FieldMappings } from '@erp/shared-types';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import type { SelectDropdownOption } from 'src/components/common/SelectDropdown';
import { RebateAnalyticsCollapseToggle } from '@/components/rebates/RebateAnalyticsCollapseToggle';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import FieldMappingTable from '../upload-data/components/FieldMappingTable';
import {
  buildUnmatchedPurchaseMappings,
  hasUnmatchedPurchaseRows,
  purchaseMappingsToSave,
} from './purchaseTrendPage';
import { useUpsertAliases } from '@/api/core/aliases/useAliases';
import { orpc } from '@/api/rpc/client';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import { PurchaseTrendCollapsibleCard } from './PurchaseTrendAppliedMappingsSection';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';

function unmatchedSupplierLabel(row: PurchaseTrendUnmatchedSupplier): string {
  return `${row.supplierCode} ${row.supplierName}`;
}

function ForecastUnavailableCopy({
  reason,
}: {
  reason: PurchaseTrendForecastUnavailableReason;
}) {
  if (reason === 'no_complete_months') {
    return (
      <T context="forecast unavailable because no complete purchase months exist">
        Forecast purchases are hidden because there are no complete months to
        calculate growth.
      </T>
    );
  }
  if (reason === 'prior_year_ytd_zero') {
    return (
      <T context="forecast unavailable because prior-year year-to-date is zero">
        Forecast purchases are hidden because prior-year year-to-date purchases
        are zero.
      </T>
    );
  }
  return (
    <T context="forecast unavailable because the supplier has no prior-year amounts">
      Forecast purchases are hidden because this supplier has no prior-year
      amounts.
    </T>
  );
}

function UnmatchedSupplierList({
  rows,
  testId,
  children,
}: {
  rows: PurchaseTrendUnmatchedSupplier[];
  testId: string;
  children: ReactNode;
}) {
  if (rows.length === 0) {
    return null;
  }
  return (
    <div data-testid={testId}>
      <p>{children}</p>
      <ul className="list-disc list-inside space-y-1 text-muted-foreground">
        {rows.map((row) => (
          <li key={row.supplierCode}>{unmatchedSupplierLabel(row)}</li>
        ))}
      </ul>
    </div>
  );
}

function mappingOptions(options: MultiSelectOption[]): SelectDropdownOption[] {
  return options.map((option) => ({
    id: option.id,
    label: option.label,
    display: option.label,
    value: option,
  }));
}

type PurchaseMappingSaveRow = ReturnType<typeof purchaseMappingsToSave>[number];

function UnmatchedMappingEditor({
  gt,
  mappings,
  setMappings,
  supplierOptions,
  divisionOptions,
  canSave,
  isSaving,
  pendingSave,
  onSave,
}: {
  gt: (message: string, options: { context: string }) => string;
  mappings: FieldMappings;
  setMappings: Dispatch<SetStateAction<FieldMappings>>;
  supplierOptions: MultiSelectOption[];
  divisionOptions: MultiSelectOption[];
  canSave: boolean;
  isSaving: boolean;
  pendingSave: PurchaseMappingSaveRow[];
  onSave: (rows: PurchaseMappingSaveRow[]) => void;
}) {
  return (
    <div data-testid="unmatched-in-file-not-in-gently">
      <FieldMappingTable
        mappings={mappings}
        setMappings={setMappings}
        supportsSplit={false}
        showIntro={false}
        mappingTypeConfigs={{
          supplierMappings: {
            displayName: gt('Unmatched suppliers', {
              context: 'mapping section for unmatched purchase vendors',
            }),
            options: mappingOptions(supplierOptions),
            placeholder: gt('Select supplier...', {
              context: 'placeholder for unmatched purchase vendor mapping',
            }),
          },
          divisionMappings: {
            displayName: gt('Unmatched divisions', {
              context: 'mapping section for unmatched purchase divisions',
            }),
            options: mappingOptions(divisionOptions),
            placeholder: gt('Select division...', {
              context: 'placeholder for unmatched purchase division mapping',
            }),
          },
        }}
      />
      {canSave ? (
        <Button
          type="button"
          size="sm"
          data-testid="save-purchase-mappings"
          disabled={isSaving || pendingSave.length === 0}
          className="mt-3 gap-2"
          onClick={() => onSave(pendingSave)}
        >
          {isSaving ? (
            <>
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
              <T context="unmatched purchase vendor and division mappings are saving">
                Saving...
              </T>
            </>
          ) : (
            <T context="save unmatched purchase vendor and division mappings">
              Save mappings
            </T>
          )}
        </Button>
      ) : null}
    </div>
  );
}

function unmatchedMappingLabels(
  gt: (message: string, options: { context: string }) => string,
) {
  return {
    vendorNumber: gt('Vendor number', {
      context: 'unmatched purchase vendor number column',
    }),
    vendorName: gt('Vendor name', {
      context: 'unmatched purchase vendor name column',
    }),
    amount: gt('Amount', {
      context: 'unmatched purchase vendor amount column',
    }),
    division: gt('Division', {
      context: 'unmatched purchase division column',
    }),
    branch: gt('Branch', {
      context: 'unmatched purchase division branch column',
    }),
    supplier: gt('Supplier', {
      context: 'unmatched purchase supplier mapping target',
    }),
  };
}

function UnmatchedMappingAlert({
  unmatchedPurchaseSuppliers,
  unmatchedDivisionNames,
  unmatchedPurchaseDivisions,
  supplierOptions,
  divisionOptions,
}: {
  unmatchedPurchaseSuppliers: PurchaseTrendUnmatchedSupplier[];
  unmatchedDivisionNames: string[];
  unmatchedPurchaseDivisions: Array<{
    divisionName: string;
    branch: string | null;
  }>;
  supplierOptions: MultiSelectOption[];
  divisionOptions: MultiSelectOption[];
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const labels = useMemo(() => unmatchedMappingLabels(gt), [gt]);
  const upsert = useUpsertAliases();
  const [canSave] = useCan(Action.Execute, Subject.RebateDataUpload);
  const initialMappings = useMemo(
    () =>
      buildUnmatchedPurchaseMappings({
        suppliers: unmatchedPurchaseSuppliers,
        divisions:
          unmatchedPurchaseDivisions.length > 0
            ? unmatchedPurchaseDivisions
            : unmatchedDivisionNames,
        labels,
        supplierOptions,
        divisionOptions,
      }),
    [
      labels,
      unmatchedDivisionNames,
      unmatchedPurchaseDivisions,
      unmatchedPurchaseSuppliers,
      supplierOptions,
      divisionOptions,
    ],
  );
  const [mappings, setMappings] = useState(initialMappings);
  const [mappingsSource, setMappingsSource] = useState(initialMappings);
  if (mappingsSource !== initialMappings) {
    setMappingsSource(initialMappings);
    setMappings(initialMappings);
  }
  const pendingSave = purchaseMappingsToSave({
    mappings,
    supplierOptions,
    divisionOptions,
  });
  if (
    !hasUnmatchedPurchaseRows({
      unmatchedPurchaseSuppliers,
      unmatchedDivisionNames,
      unmatchedPurchaseDivisions,
    })
  ) {
    return null;
  }
  return (
    <AlertContainer className="mb-2" data-testid="unmatched-purchase-mappings">
      <CardTitle className="col-start-2 flex items-center gap-1.5 text-base">
        <RebateAnalyticsCollapseToggle
          isCollapsed={collapsed}
          onToggle={() => setCollapsed((open) => !open)}
          testId="unmatched-mappings-collapse"
        />
        <T context="error title for unmatched purchase file vendors and divisions">
          Unmatched file data
        </T>
      </CardTitle>
      {collapsed ? null : (
        <AlertDescription>
          <T context="error that unmatched purchase vendors and divisions need a Gently match and are saved only when the user confirms">
            These vendors and divisions from the file are not in Gently. Pick a
            Gently supplier or division for each. Suggestions are not saved
            until you confirm.
          </T>
          <UnmatchedMappingEditor
            gt={gt}
            mappings={mappings}
            setMappings={setMappings}
            supplierOptions={supplierOptions}
            divisionOptions={divisionOptions}
            canSave={canSave}
            isSaving={isSaving}
            pendingSave={pendingSave}
            onSave={(rows) => {
              setIsSaving(true);
              void Promise.all(rows.map((row) => upsert.mutateAsync(row)))
                .then(() =>
                  queryClient.invalidateQueries({
                    queryKey: orpc.rebates.purchaseTrend.get.key(),
                  }),
                )
                .catch(() => undefined)
                .finally(() => setIsSaving(false));
            }}
          />
        </AlertDescription>
      )}
    </AlertContainer>
  );
}

export function PurchaseTrendNoAgreementSuppliersSection({
  purchaseSuppliersWithoutRebateAgreement,
}: {
  purchaseSuppliersWithoutRebateAgreement: PurchaseTrendUnmatchedSupplier[];
}) {
  if (purchaseSuppliersWithoutRebateAgreement.length === 0) {
    return null;
  }
  return (
    <PurchaseTrendCollapsibleCard
      testId="unmatched-purchase-suppliers"
      collapseTestId="unmatched-suppliers-collapse"
      title={
        <T context="card title for matched purchase suppliers with no rebate agreement">
          Suppliers with no rebate agreement
        </T>
      }
      summary={
        <span
          className="font-normal text-destructive"
          data-testid="unmatched-purchase-suppliers-count"
        >
          <T context="count of matched purchase suppliers with no rebate agreement">
            <Num>{purchaseSuppliersWithoutRebateAgreement.length}</Num>{' '}
            <Plural
              n={purchaseSuppliersWithoutRebateAgreement.length}
              one="supplier"
              other="suppliers"
            />
          </T>
        </span>
      }
    >
      <UnmatchedSupplierList
        rows={purchaseSuppliersWithoutRebateAgreement}
        testId="unmatched-in-gently-no-agreement"
      >
        <T context="label for matched suppliers with no rebate agreement">
          In Gently, no rebate agreement
        </T>
      </UnmatchedSupplierList>
    </PurchaseTrendCollapsibleCard>
  );
}

export function PurchaseTrendFlags({
  forecastUnavailableReason,
  unmatchedPurchaseSuppliers,
  unmatchedDivisionNames,
  unmatchedPurchaseDivisions = [],
  supplierOptions,
  divisionOptions,
  isShared = false,
}: {
  forecastUnavailableReason: PurchaseTrendForecastUnavailableReason | null;
  unmatchedPurchaseSuppliers: PurchaseTrendUnmatchedSupplier[];
  unmatchedDivisionNames: string[];
  unmatchedPurchaseDivisions?: Array<{
    divisionName: string;
    branch: string | null;
  }>;
  supplierOptions: MultiSelectOption[];
  divisionOptions: MultiSelectOption[];
  isShared?: boolean;
}) {
  return (
    <>
      {forecastUnavailableReason ? (
        <AlertContainer
          className="mb-2"
          data-testid="forecast-unavailable-flag"
        >
          <CardTitle className="col-start-2 text-base">
            <T context="notice title when purchase forecast cannot be calculated">
              Forecast unavailable
            </T>
          </CardTitle>
          <AlertDescription>
            <ForecastUnavailableCopy reason={forecastUnavailableReason} />
          </AlertDescription>
        </AlertContainer>
      ) : null}
      {isShared ? null : (
        <UnmatchedMappingAlert
          unmatchedPurchaseSuppliers={unmatchedPurchaseSuppliers}
          unmatchedDivisionNames={unmatchedDivisionNames}
          unmatchedPurchaseDivisions={unmatchedPurchaseDivisions}
          supplierOptions={supplierOptions}
          divisionOptions={divisionOptions}
        />
      )}
    </>
  );
}
